"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiSend, FiUser } from "react-icons/fi";
import { Button, DollarInput } from "@/components/ui";
import QuantityStepper from "@/components/ui/quantity-stepper";
import createId from "@/lib/create-id";
import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import { GiftCardRecipientInput, GiftCardShopCartLine, ShopItem } from "@/types/shop";
import RecipientForm, { RecipientFormHandle } from "./recipient-form";
const makeLineId = (): string => createId("cl");
const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = t0 => {
  const $ = _c(5);
  const {
    children,
    required
  } = t0;
  let t1;
  if ($[0] !== required) {
    t1 = required && <span className="ml-0.5 text-status-error-text">*</span>;
    $[0] = required;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <label className="text-body-strong text-foreground">{children}{t1}</label>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/**
 * Per-card decision in the stepper. Cards default to `keep` so a buyer who
 * just clicks Next on every step ends up with all cards for themselves.
 * `recipient` is preserved across mode toggles so flipping keep→send→keep
 * doesn't blow away a half-filled form.
 */
type CardDecision = {
  mode: "keep" | "send";
  recipient?: GiftCardRecipientInput;
};
const newKeepDecision = (): CardDecision => ({
  mode: "keep"
});
const isCardComplete = (d: CardDecision): boolean => d.mode === "keep" || d.recipient !== undefined;

/**
 * Decide the starting decisions array when editing an existing cart line.
 * Cards are anonymous, so we pack any saved recipients into the first L
 * positions and leave the remaining quantity−L slots as keep-for-self.
 */
const decisionsFromLine = (line: GiftCardShopCartLine | undefined, quantity: number): CardDecision[] => {
  const recipients = line?.recipients ?? [];
  return Array.from({
    length: quantity
  }, (_, i): CardDecision => {
    const r = recipients[i];
    return r ? {
      mode: "send",
      recipient: r
    } : newKeepDecision();
  });
};
interface StepIndicatorProps {
  current: number;
  decisions: CardDecision[];
}

/**
 * Linear dot/segment indicator across the cards. Hidden at quantity=1
 * since there's only one step to show.
 *
 * A step is "complete" only once the user has navigated past it — not
 * just because its decision happens to be valid. Otherwise every step
 * past `current` would render with a checkmark (cards default to
 * `keep`, which is always valid), making the indicator misleadingly
 * say "all done" before the user has even seen those cards.
 *
 * At high quantities the row would overflow the dialog; we wrap it in
 * a horizontally scrollable strip and scroll the current step into
 * view whenever it changes.
 */
const StepIndicator: React.FC<StepIndicatorProps> = t0 => {
  const $ = _c(11);
  const {
    current,
    decisions
  } = t0;
  const containerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== current) {
    t1 = () => {
      const el = containerRef.current?.querySelector(`[data-step-index="${current}"]`);
      el?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });
    };
    t2 = [current];
    $[0] = current;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (decisions.length <= 1) {
    return null;
  }
  let t3;
  if ($[3] !== current || $[4] !== decisions) {
    let t4;
    if ($[6] !== current || $[7] !== decisions.length) {
      t4 = (d, i) => {
        const isCurrent = i === current;
        const isComplete = i < current && isCardComplete(d);
        return <React.Fragment key={i}><li data-step-index={i} aria-current={isCurrent ? "step" : undefined} className={cx("flex h-5 min-w-5 flex-none items-center justify-center rounded-full px-1.5 text-tiny font-semibold", isCurrent && "bg-primary text-primary-foreground", isComplete && "bg-status-success-bg text-status-success-text", !isCurrent && !isComplete && "bg-background-secondary text-foreground-secondary")}>{isComplete ? <FiCheck className="h-3 w-3" /> : i + 1}</li>{i < decisions.length - 1 && <span className="h-px w-3 flex-none bg-border" aria-hidden={true} />}</React.Fragment>;
      };
      $[6] = current;
      $[7] = decisions.length;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = decisions.map(t4);
    $[3] = current;
    $[4] = decisions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <ol ref={containerRef} className="flex max-w-[12rem] items-center gap-1.5 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden" aria-label="Card progress">{t3}</ol>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
interface ModeToggleProps {
  mode: CardDecision["mode"];
  onModeChange: (mode: CardDecision["mode"]) => void;
}

/**
 * Compact segmented control for the keep/send choice. Pill-shaped, icon +
 * label only — the labels are self-explanatory enough that subtitles just
 * add visual noise.
 */
const ModeToggle: React.FC<ModeToggleProps> = t0 => {
  const $ = _c(4);
  const {
    mode,
    onModeChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      value: "keep",
      label: "Keep for me",
      Icon: FiUser
    }, {
      value: "send",
      label: "Send to someone else",
      Icon: FiSend
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const options = t1;
  let t2;
  if ($[1] !== mode || $[2] !== onModeChange) {
    t2 = <div role="radiogroup" aria-label="Who is this gift card for?" className="inline-flex rounded-full border border-border bg-background-secondary p-0.5">{options.map(opt => {
        const isActive = mode === opt.value;
        return <button key={opt.value} type="button" role="radio" aria-checked={isActive} onClick={() => onModeChange(opt.value)} className={cx("inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-body-strong transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary", isActive ? "bg-background text-foreground shadow-sm" : "text-foreground-secondary hover:text-foreground")}><opt.Icon className="h-3.5 w-3.5" /><span>{opt.label}</span></button>;
      })}</div>;
    $[1] = mode;
    $[2] = onModeChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export interface GiftCardConfiguratorProps {
  /** Catalog item being configured, or `null` for a custom-amount line. */
  item: ShopItem | null;
  /** Existing cart line being edited; the configurator pre-fills from it. */
  initialLine?: GiftCardShopCartLine;
  onConfirm: (line: GiftCardShopCartLine) => void;
  onCancel: () => void;
}
const GiftCardConfigurator: React.FC<GiftCardConfiguratorProps> = t0 => {
  const $ = _c(123);
  const {
    item,
    initialLine,
    onConfirm,
    onCancel
  } = t0;
  const isCustom = item === null;
  const initialIsCustom = initialLine?.kind === "custom";
  const initialAmountDollars = initialIsCustom && initialLine ? initialLine.amountCents / 100 : 0;
  const [amountDollars, setAmountDollars] = useState(initialAmountDollars);
  const [quantity, setQuantity] = useState(initialLine?.quantity ?? 1);
  let t1;
  if ($[0] !== initialLine) {
    t1 = () => decisionsFromLine(initialLine, initialLine?.quantity ?? 1);
    $[0] = initialLine;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [decisions, setDecisions] = useState(t1);
  const [activeIndex, setActiveIndex] = useState(0);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const formRef = useRef(null);
  let t2;
  if ($[2] !== amountDollars || $[3] !== isCustom || $[4] !== item) {
    t2 = isCustom ? Math.round(amountDollars * 100) : item.price;
    $[2] = amountDollars;
    $[3] = isCustom;
    $[4] = item;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const amountCents = t2;
  const amountValid = amountCents > 0;
  const total = amountCents * quantity;
  const isEditing = Boolean(initialLine);
  const active = decisions[activeIndex];
  const isLastCard = activeIndex === decisions.length - 1;
  let t3;
  if ($[6] !== decisions) {
    t3 = decisions.every(isCardComplete);
    $[6] = decisions;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const allCardsComplete = t3;
  const canConfirm = amountValid && allCardsComplete;
  let t4;
  if ($[8] !== quantity) {
    t4 = n => {
      const next = Math.max(1, n);
      if (next === quantity) {
        return;
      }
      setQuantity(next);
      setDecisions(prev => {
        if (next === prev.length) {
          return prev;
        }
        if (next > prev.length) {
          return [...prev, ...Array.from({
            length: next - prev.length
          }, newKeepDecision)];
        }
        return prev.slice(0, next);
      });
      setActiveIndex(i => Math.min(i, next - 1));
    };
    $[8] = quantity;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleQuantityChange = t4;
  let t5;
  if ($[10] !== activeIndex) {
    const updateActive = updater => {
      setDecisions(prev_0 => prev_0.map((d, i_0) => i_0 === activeIndex ? updater(d) : d));
    };
    t5 = mode => {
      updateActive(d_0 => ({
        ...d_0,
        mode
      }));
    };
    $[10] = activeIndex;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleModeChange = t5;
  let t6;
  if ($[12] !== amountCents || $[13] !== amountValid || $[14] !== initialLine?.lineId || $[15] !== isCustom || $[16] !== item || $[17] !== onConfirm || $[18] !== quantity) {
    t6 = finalDecisions => {
      setSubmitAttempted(true);
      if (!amountValid || !finalDecisions.every(isCardComplete)) {
        return;
      }
      const recipients = finalDecisions.flatMap(_temp);
      const lineId = initialLine?.lineId ?? makeLineId();
      const line = isCustom ? {
        kind: "custom",
        lineId,
        itemType: ItemType.GIFT_CARD,
        amountCents,
        quantity,
        recipients,
        snapshot: {
          name: `Custom ${formatCurrency(amountCents)} digital gift card`,
          description: null
        }
      } : {
        kind: "preset",
        lineId,
        itemType: ItemType.GIFT_CARD,
        itemId: item.id,
        unitPriceCents: item.price,
        quantity,
        recipients,
        snapshot: {
          name: item.name,
          description: item.description
        }
      };
      onConfirm(line);
    };
    $[12] = amountCents;
    $[13] = amountValid;
    $[14] = initialLine?.lineId;
    $[15] = isCustom;
    $[16] = item;
    $[17] = onConfirm;
    $[18] = quantity;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const finalize = t6;
  let t7;
  if ($[20] !== activeIndex || $[21] !== decisions || $[22] !== finalize) {
    t7 = recipient => {
      const nextDecisions = decisions.map((d_2, i_1) => i_1 === activeIndex ? {
        mode: "send" as const,
        recipient
      } : d_2);
      setDecisions(nextDecisions);
      if (activeIndex < nextDecisions.length - 1) {
        setActiveIndex(activeIndex + 1);
      } else {
        finalize(nextDecisions);
      }
    };
    $[20] = activeIndex;
    $[21] = decisions;
    $[22] = finalize;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const handleRecipientSave = t7;
  let t8;
  if ($[24] !== active.mode || $[25] !== activeIndex || $[26] !== amountValid || $[27] !== decisions || $[28] !== finalize) {
    t8 = async () => {
      setSubmitAttempted(true);
      if (!amountValid) {
        return;
      }
      if (active.mode === "send") {
        await formRef.current?.submit();
        return;
      }
      if (activeIndex < decisions.length - 1) {
        setActiveIndex(activeIndex + 1);
      } else {
        finalize(decisions);
      }
    };
    $[24] = active.mode;
    $[25] = activeIndex;
    $[26] = amountValid;
    $[27] = decisions;
    $[28] = finalize;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const handleNext = t8;
  let t9;
  if ($[30] !== activeIndex) {
    t9 = () => {
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      }
    };
    $[30] = activeIndex;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const handleBack = t9;
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setSubmitAttempted(false);
    };
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== activeIndex) {
    t11 = [activeIndex];
    $[33] = activeIndex;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  useEffect(t10, t11);
  const cardHeader = decisions.length === 1 ? "Who is this gift card for?" : `Card ${activeIndex + 1} of ${decisions.length}`;
  const t12 = isCustom ? "Custom digital gift card" : item.name;
  let t13;
  if ($[35] !== t12) {
    t13 = <h3 className="text-h2 tracking-tight text-foreground">{t12}</h3>;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== isCustom || $[38] !== item) {
    t14 = !isCustom && item.giftCardRestrictionLabel && <p className="text-body-strong text-foreground">Redeemable for {item.giftCardRestrictionLabel}.</p>;
    $[37] = isCustom;
    $[38] = item;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== isCustom || $[41] !== item) {
    t15 = !isCustom && item.description && <p className="text-body text-foreground-secondary">{item.description}</p>;
    $[40] = isCustom;
    $[41] = item;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== isCustom) {
    t16 = isCustom && <p className="text-body text-foreground-secondary">Pick any amount above $0.</p>;
    $[43] = isCustom;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t13 || $[46] !== t14 || $[47] !== t15 || $[48] !== t16) {
    t17 = <header className="space-y-2">{t13}{t14}{t15}{t16}</header>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== amountDollars || $[51] !== amountValid || $[52] !== isCustom || $[53] !== submitAttempted) {
    t18 = isCustom && <div className="space-y-1.5"><FieldLabel required={true}>Amount</FieldLabel><DollarInput value={amountDollars} onValueChange={setAmountDollars} placeholder="0.00" hasError={submitAttempted && !amountValid} errorMessage={submitAttempted && !amountValid ? "Enter an amount greater than $0." : undefined} /></div>;
    $[50] = amountDollars;
    $[51] = amountValid;
    $[52] = isCustom;
    $[53] = submitAttempted;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FieldLabel>Quantity</FieldLabel>;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== handleQuantityChange || $[57] !== quantity) {
    t20 = <div className="flex items-center justify-between">{t19}<QuantityStepper value={quantity} onChange={handleQuantityChange} /></div>;
    $[56] = handleQuantityChange;
    $[57] = quantity;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t18 || $[60] !== t20) {
    t21 = <div className="space-y-5">{t18}{t20}</div>;
    $[59] = t18;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== cardHeader) {
    t22 = <h4 id="active-card-heading" className="text-body-emphasis text-foreground">{cardHeader}</h4>;
    $[62] = cardHeader;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== activeIndex || $[65] !== decisions) {
    t23 = <StepIndicator current={activeIndex} decisions={decisions} />;
    $[64] = activeIndex;
    $[65] = decisions;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== t22 || $[68] !== t23) {
    t24 = <div className="flex items-center justify-between gap-3">{t22}{t23}</div>;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== active.mode || $[71] !== handleModeChange) {
    t25 = <ModeToggle mode={active.mode} onModeChange={handleModeChange} />;
    $[70] = active.mode;
    $[71] = handleModeChange;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== active.mode || $[74] !== active.recipient || $[75] !== activeIndex || $[76] !== handleRecipientSave) {
    t26 = active.mode === "send" && <motion.div key="send-form" initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      height: {
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1]
      },
      opacity: {
        duration: 0.18,
        ease: "easeOut"
      }
    }} style={{
      overflow: "hidden"
    }}><RecipientForm key={`form-${activeIndex}`} ref={formRef} initial={active.recipient} hideSubmitButton={true} onSave={handleRecipientSave} /></motion.div>;
    $[73] = active.mode;
    $[74] = active.recipient;
    $[75] = activeIndex;
    $[76] = handleRecipientSave;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] !== t26) {
    t27 = <AnimatePresence initial={false}>{t26}</AnimatePresence>;
    $[78] = t26;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== active.mode || $[81] !== active.recipient || $[82] !== submitAttempted) {
    t28 = submitAttempted && active.mode === "send" && !active.recipient && <p className="text-caption text-status-error-text">Add the recipient's details before continuing.</p>;
    $[80] = active.mode;
    $[81] = active.recipient;
    $[82] = submitAttempted;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== t24 || $[85] !== t25 || $[86] !== t27 || $[87] !== t28) {
    t29 = <section aria-labelledby="active-card-heading" className="space-y-4">{t24}{t25}{t27}{t28}</section>;
    $[84] = t24;
    $[85] = t25;
    $[86] = t27;
    $[87] = t28;
    $[88] = t29;
  } else {
    t29 = $[88];
  }
  let t30;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="text-overline text-foreground-secondary">Total</div>;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  const t31 = Math.max(0, total);
  let t32;
  if ($[90] !== t31) {
    t32 = formatCurrency(t31);
    $[90] = t31;
    $[91] = t32;
  } else {
    t32 = $[91];
  }
  let t33;
  if ($[92] !== t32) {
    t33 = <div>{t30}<div className="text-h1 tracking-tight text-foreground">{t32}</div></div>;
    $[92] = t32;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span>Cancel</span>;
    $[94] = t34;
  } else {
    t34 = $[94];
  }
  let t35;
  if ($[95] !== onCancel) {
    t35 = <Button variant="secondary" onClick={onCancel} className="cursor-pointer">{t34}</Button>;
    $[95] = onCancel;
    $[96] = t35;
  } else {
    t35 = $[96];
  }
  let t36;
  if ($[97] !== activeIndex || $[98] !== handleBack) {
    t36 = activeIndex > 0 && <Button variant="secondary" onClick={handleBack} className="cursor-pointer"><span>Back</span></Button>;
    $[97] = activeIndex;
    $[98] = handleBack;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  const t37 = !amountValid;
  const t38 = isLastCard ? isEditing ? "Update cart" : "Add to cart" : "Next";
  let t39;
  if ($[100] !== t38) {
    t39 = <span>{t38}</span>;
    $[100] = t38;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== handleNext || $[103] !== t37 || $[104] !== t39) {
    t40 = <Button variant="primary" onClick={handleNext} disabled={t37} className="cursor-pointer">{t39}</Button>;
    $[102] = handleNext;
    $[103] = t37;
    $[104] = t39;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== t35 || $[107] !== t36 || $[108] !== t40) {
    t41 = <div className="flex flex-wrap items-center justify-end gap-2">{t35}{t36}{t40}</div>;
    $[106] = t35;
    $[107] = t36;
    $[108] = t40;
    $[109] = t41;
  } else {
    t41 = $[109];
  }
  let t42;
  if ($[110] !== t33 || $[111] !== t41) {
    t42 = <footer className="flex items-center justify-between gap-4 border-t border-border pt-6">{t33}{t41}</footer>;
    $[110] = t33;
    $[111] = t41;
    $[112] = t42;
  } else {
    t42 = $[112];
  }
  let t43;
  if ($[113] !== canConfirm || $[114] !== isLastCard || $[115] !== submitAttempted) {
    t43 = submitAttempted && !canConfirm && isLastCard && <p className="-mt-2 text-right text-caption text-status-error-text">Some cards still need a decision.</p>;
    $[113] = canConfirm;
    $[114] = isLastCard;
    $[115] = submitAttempted;
    $[116] = t43;
  } else {
    t43 = $[116];
  }
  let t44;
  if ($[117] !== t17 || $[118] !== t21 || $[119] !== t29 || $[120] !== t42 || $[121] !== t43) {
    t44 = <div className="space-y-6">{t17}{t21}{t29}{t42}{t43}</div>;
    $[117] = t17;
    $[118] = t21;
    $[119] = t29;
    $[120] = t42;
    $[121] = t43;
    $[122] = t44;
  } else {
    t44 = $[122];
  }
  return t44;
};
export default GiftCardConfigurator;
function _temp(d_1) {
  return d_1.mode === "send" && d_1.recipient ? [d_1.recipient] : [];
}